import React, { useState, useEffect } from 'react';
import { Box, Drawer, Card, CardContent, Typography, CardHeader, CircularProgress } from '@mui/material';
import api from "../../../../services/api";
import FormAddEditToolbar from "../../../../components/global/AddEditBar";
import { useNavigate } from 'react-router-dom';
import { useNotifier } from "muinotify";
import RecordingsForm from "../RecordingsForm";
import useSWR, { mutate } from 'swr';
import ErrorNotification from '../../../../components/ErrorNotification/ErrorNotification';
import * as yup from 'yup';

const validationSchema = yup.object().shape({
    p_id: yup
        .number()
        .typeError('Η παραγωγή πρέπει να είναι αριθμός')
        .required('Η παραγωγή απαιτείται')
        .positive('Η παραγωγή πρέπει να είναι θετικός αριθμός')
        .integer('Η παραγωγή πρέπει να είναι ακέραιος αριθμός')
        .transform((value, originalValue) => {
            // Convert empty string, null, or undefined to undefined (which will fail required)
            if (originalValue === "" || originalValue === null || originalValue === undefined) {
                return undefined;
            }
            // Convert to number
            const numValue = Number(originalValue);
            return isNaN(numValue) ? undefined : numValue;
        }),

    rec_number: yup
        .number()
        .required('Ο αριθμός εγγραφής απαιτείται')
        .positive('Ο αριθμός εγγραφής πρέπει να είναι θετικός αριθμός')
        .integer('Ο αριθμός εγγραφής πρέπει να είναι ακέραιος'),

    rec_date: yup
        .date()
        .nullable(true),

    episodes: yup
        .array()
        .of(yup.number().positive().integer())
        .nullable(true),

    rec_notes: yup
        .string()
        .nullable(true),
});

const RecordingsAdd = () => {
    const navigate = useNavigate();
    const { data: productions } = useSWR(`/productions`);
    const { notifier } = useNotifier();

    const [state, setState] = useState({
        p_id: null,
        rec_number: 1,
        rec_date: null,
        episodes: [],
        rec_notes: null,
    });

    const headerNames = {
        p_id: 'Παραγωγή',
        rec_number: 'Αριθμός Εγγραφής',
        rec_date: 'Ημερομηνία Εγγραφής',
        episodes: 'Επεισόδια',
        rec_notes: 'Σημειώσεις'
    };

    const URL = `recordings`;
    const NAV_PATH = `recordings`;

    const [errorMessage, setErrorMessage] = useState();
    const [errors, setErrors] = useState({});
    const [isUpdating, setIsUpdating] = useState(false);

    const [previousPId, setPreviousPId] = useState(null);

    // Fetch recordings when production is selected to calculate next rec_number
    const { data: existingRecordings } = useSWR(
        state.p_id ? `/recordings/of-production/${state.p_id}` : null
    );

    // Auto-calculate rec_number when production changes
    useEffect(() => {
        if (state.p_id && state.p_id !== previousPId) {
            if (existingRecordings) {
                const maxRecNumber = existingRecordings.length > 0
                    ? Math.max(...existingRecordings.map(r => r.rec_number || 0))
                    : 0;
                const nextRecNumber = maxRecNumber + 1;
                setState(prev => ({ ...prev, rec_number: nextRecNumber }));
            } else {
                // If no recordings exist yet, start from 1
                setState(prev => ({ ...prev, rec_number: 1 }));
            }
            setPreviousPId(state.p_id);
        }
    }, [state.p_id, existingRecordings, previousPId]);

    function updateField(fieldName, value) {
        setState(prevState => ({ ...prevState, [fieldName]: value }));
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Normalize p_id to ensure it's a number before validation
            const normalizedState = {
                ...state,
                p_id: state.p_id ? Number(state.p_id) : undefined,
                rec_number: state.rec_number ? Number(state.rec_number) : undefined,
            };
            
            console.log('Validation state:', normalizedState);
            console.log('p_id value:', normalizedState.p_id, 'type:', typeof normalizedState.p_id);
            
            await validationSchema.validate(normalizedState, { abortEarly: false });
            setErrors({});
            setIsUpdating(true);
            
            // Prepare data for API
            const submitData = {
                ...normalizedState,
                rec_date: normalizedState.rec_date || null,
            };

            api.post(`/${URL}/`, submitData)
                .then(response => {
                    mutate(`/${NAV_PATH}`);
                    mutate(`/${URL}/of-production/${normalizedState.p_id}`);
                    notifier("Νέα εγγραφή δημιουργήθηκε.", { variant: "success" });
                    setDrawerOpen(false);
                    navigate(-1);
                })
                .catch(error => {
                    setErrorMessage(error);
                    notifier(<ErrorNotification error={error} />, { variant: "error" });
                })
                .finally(() => {
                    setIsUpdating(false);
                });
        } catch (validationErrors) {
            const formattedErrors = {};
            validationErrors.inner.forEach((error) => {
                formattedErrors[error.path] = error.message;
            });
            setErrors(formattedErrors);
        }
    };

    const handleSave = () => {
        const form = document.getElementById(`${URL}-form`);
        if (form) {
            form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    const [drawerOpen, setDrawerOpen] = useState(true);

    return (
        <>
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={handleCancel}
                PaperProps={{
                    style: {
                        width: '100%',
                        height: '100vh',
                        maxWidth: '800px',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <Card
                    sx={{
                        width: '100%',
                        maxWidth: 800,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    {/* Header Section */}
                    <Box>
                        <CardHeader
                            title={
                                <Typography variant="h3" gutterBottom>
                                    Δημιουργία Εγγραφής
                                </Typography>
                            }
                        />
                    </Box>

                    {/* Scrollable Content Section */}
                    <CardContent
                        sx={{
                            display: 'flex',
                            height: '100vh',
                            flexGrow: 1,
                            overflowY: 'auto',
                            margin: 'auto',
                        }}
                    >
                        <form id={`${URL}-form`} onSubmit={handleSubmit}>
                            <RecordingsForm
                                state={state}
                                errors={errors}
                                updateField={updateField}
                                headerNames={headerNames}
                                productions={productions}
                                existingRecordings={existingRecordings}
                            />
                        </form>
                        {isUpdating && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                    zIndex: 1000,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        )}
                    </CardContent>

                    {/* Toolbar (Footer Section) */}
                    <Box>
                        <FormAddEditToolbar
                            handleSave={handleSave}
                            handleCancel={handleCancel}
                        />
                    </Box>
                </Card>
            </Drawer>
        </>
    );
};

export default RecordingsAdd;

